// imagenAjuste45.js — P2b tanda 3e (ajuste 8), generalizado a RATIO en P3-12. Llevar una foto de
// PRODUCTO a la proporción de SU TIPO (prenda 4:5, cosmético/consumible 1:1, o la configurada)
// ANTES de subir. Dos caminos: RECORTE (marco móvil/escalable) o MARGEN (rellenar con el color del
// fondo). Piezas PURAS; el canvas vive en el selector. Exactitud: alto múltiplo de `ratioH` y
// ancho = alto·ratioW/ratioH, así la proporción es EXACTA en píxeles enteros.
//
// El RATIO por defecto es 4:5 (lo que era fijo antes de P3-12): un llamado sin ratio se comporta
// como la prenda, así que nada que no pase ratio cambia.

// Marco de recorte (ratioW:ratioH) dentro de la foto. cx/cy = centro del marco (0..1); escala =
// tamaño relativo al máximo (0..1). SIEMPRE un rect exacto en la proporción, dentro de la foto.
export function rectDeRecorte(w, h, { cx = 0.5, cy = 0.5, escala = 1, ratioW = 4, ratioH = 5 } = {}) {
  const baseH = Math.floor(Math.min(h, (w * ratioH) / ratioW) / ratioH) * ratioH;
  const esc = Math.min(Math.max(Number(escala) || 1, 0.1), 1);
  let fh = Math.floor((baseH * esc) / ratioH) * ratioH;
  if (fh < ratioH) fh = ratioH;
  const fw = (fh / ratioH) * ratioW;
  let x = Math.round(cx * w - fw / 2);
  let y = Math.round(cy * h - fh / 2);
  x = Math.min(Math.max(0, x), w - fw);
  y = Math.min(Math.max(0, y), h - fh);
  return { x, y, width: fw, height: fh };
}

// El marco por defecto: el más grande posible, centrado.
export function recorteCentrado(w, h, ratioW = 4, ratioH = 5) {
  return rectDeRecorte(w, h, { ratioW, ratioH });
}

// Lienzo (ratioW:ratioH) mínimo que contiene la foto ENTERA sin deformarla, centrada (dx/dy =
// margen izq/sup). El relleno lo pinta el caller.
export function margenARatio(w, h, ratioW = 4, ratioH = 5) {
  let width, height;
  if (w / h <= ratioW / ratioH) {
    height = Math.ceil(h / ratioH) * ratioH;
    width = (height / ratioH) * ratioW;
  } else {
    width = Math.ceil(w / ratioW) * ratioW;
    height = (width / ratioW) * ratioH;
  }
  return { width, height, dx: Math.floor((width - w) / 2), dy: Math.floor((height - h) / 2) };
}

// P3-1/P3-12 — ENCUADRE unificado sobre un lienzo (ratioW:ratioH) fijo. zoom=1 = foto entera dentro
// del lienzo; zoom>1 acerca (recorta); zoom<1 aleja (más margen). cx/cy = centro de la foto como
// fracción del lienzo. El caller pinta el fondo y dibuja en {dx,dy,dw,dh}. El lienzo se acota a
// maxW×maxH (dims de la proporción; 4:5 → 1600×2000, 1:1 → 1600×1600, 3:4 → 1600×2133).
export function encuadreRatio(w, h, { zoom = 1, cx = 0.5, cy = 0.5, maxW = 1600, maxH = 2000, ratioW = 4, ratioH = 5 } = {}) {
  const base = margenARatio(w, h, ratioW, ratioH);
  const k = Math.min(1, maxW / base.width, maxH / base.height);
  const canvasW = Math.round(base.width * k);
  const canvasH = Math.round((canvasW / ratioW) * ratioH);
  const z = Math.min(Math.max(Number(zoom) || 1, 0.25), 4);
  const dw = w * k * z;
  const dh = h * k * z;
  const fx = Math.min(Math.max(Number(cx) || 0.5, 0), 1);
  const fy = Math.min(Math.max(Number(cy) || 0.5, 0), 1);
  return {
    canvasW, canvasH,
    dx: Math.round(fx * canvasW - dw / 2),
    dy: Math.round(fy * canvasH - dh / 2),
    dw: Math.round(dw), dh: Math.round(dh),
  };
}

// Color de fondo detectado: promedio de los píxeles del BORDE. data = RGBA plano (ImageData.data).
export function colorDeBordes(data, width, height) {
  let r = 0, g = 0, b = 0, n = 0;
  const sumar = (x, y) => {
    const i = (y * width + x) * 4;
    r += data[i]; g += data[i + 1]; b += data[i + 2]; n += 1;
  };
  for (let x = 0; x < width; x++) { sumar(x, 0); if (height > 1) sumar(x, height - 1); }
  for (let y = 1; y < height - 1; y++) { sumar(0, y); if (width > 1) sumar(width - 1, y); }
  if (!n) return '#ffffff';
  const hex = (v) => Math.round(v / n).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
